'use client';

import type { BoxProps } from '@mui/material/Box';
import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { merge } from 'es-toolkit';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';

import { CONFIG } from 'src/global-config';

import { Logo as LogoBase } from 'src/components/logo';

import { SimpleCompactContent } from './content';
import { MainSection } from '../core/main-section';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';

import type { MainSectionProps } from '../core/main-section';
import type { LayoutSectionProps } from '../core/layout-section';
import type { HeaderSectionProps } from '../core/header-section';

// ----------------------------------------------------------------------

// Logo 는 아직 JS 라 props 가 전부 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const Logo = LogoBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  href?: string;
  isSingle?: boolean;
  disabled?: boolean;
  className?: string;
}>;

type SimpleLayoutProps = LayoutSectionProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    main?: MainSectionProps;
    content?: BoxProps & { compact?: boolean };
  };
};

export function SimpleLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'md',
}: SimpleLayoutProps) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const renderHeader = () => {
    const headerSlotProps = { container: { maxWidth: false } };

    const headerSlots = {
      topArea: (
        <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      leftArea: <Logo isSingle={false} sx={{ ml: 0, flexShrink: 0, position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)' }} />,
      rightArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
          {/** @slot Help link */}
          <Link
            href={CONFIG.channelTalkUrl}
            target={isClient ? "_blank" : undefined}
            rel={isClient ? "noopener noreferrer" : undefined}
            color="primary"
            sx={{ typography: 'subtitle2', fontWeight: 600, right: 20, position: 'absolute' }}
          >
            도움 요청
          </Link>

          {/** @slot Settings button */}
          {/* <SettingsButton /> */}
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderFooter = () => null;

  const renderMain = () => {
    const { compact, ...restContentProps } = slotProps?.content ?? {};

    return (
      <MainSection {...slotProps?.main}>
        {compact ? (
          <SimpleCompactContent layoutQuery={layoutQuery} {...restContentProps}>
            {children}
          </SimpleCompactContent>
        ) : (
          children
        )}
      </MainSection>
    );
  };

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{ '--layout-simple-content-compact-width': '448px', ...cssVars }}
      // 바탕은 흰색이다 — 오류 안내 한 장(404·403·500)이 서비스 메인 영역·단독 페이지와 같은 바탕에 선다
      sx={[{ bgcolor: 'background.paper' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
